import { DEFAULT_APPEARANCE_SETTINGS, getStorageKey, safeParseSettings, type AppearanceSettings } from './userSettingsStore';

export const APPEARANCE_CHANGED = 'ax1:appearance-changed';
export const UNAVAILABLE_APPEARANCE = '\u0000unavailable';
export type AppearancePreferencePatch = Partial<Pick<AppearanceSettings, 'themeMode' | 'density' | 'animations' | 'investorStartPage'>>;

const choices = {
  themeMode: ['light', 'dark', 'system'],
  density: ['compact', 'comfortable', 'spacious'],
  homePage: ['dashboard', 'projects', 'tasks', 'calendar'],
  language: ['en', 'en-gb', 'fr', 'de', 'es'],
  dateFormat: ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'],
  investorStartPage: ['auto', 'dashboard', 'investments', 'decisions', 'data-rooms'],
} as const;

export function normalizeAppearance(value: unknown): AppearanceSettings {
  const input = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const output: Record<string, unknown> = { ...DEFAULT_APPEARANCE_SETTINGS, investorStartPage: 'auto' };
  for (const [key, values] of Object.entries(choices)) {
    if (typeof input[key] === 'string' && (values as readonly string[]).includes(input[key] as string)) output[key] = input[key];
  }
  if (typeof input.animations === 'boolean') output.animations = input.animations;
  return output as unknown as AppearanceSettings;
}

/** Same actor-owned user-settings record; appearance never triggers notification sync. */
export function readAppearanceSnapshot(userId: string): string | null {
  if (!userId || userId === 'anonymous') return null;
  try { return localStorage.getItem(getStorageKey(userId)); }
  catch { return UNAVAILABLE_APPEARANCE; }
}

export function parseAppearanceSnapshot(raw: string | null) {
  const record = raw === UNAVAILABLE_APPEARANCE ? null : safeParseSettings(raw);
  const error = raw === UNAVAILABLE_APPEARANCE ? 'Browser storage is unavailable. Changes cannot be saved.'
    : raw !== null && !record ? 'Saved settings could not be read. Nothing has been reset.' : null;
  return { appearance: normalizeAppearance(record?.appearance), error };
}

export function saveAppearancePreferences(userId: string, patch: AppearancePreferencePatch): void {
  if (!userId || userId === 'anonymous') throw new Error('Sign in to save your appearance.');
  const allowed = new Set(['themeMode', 'density', 'animations', 'investorStartPage']);
  for (const [key, value] of Object.entries(patch)) {
    if (!allowed.has(key) || (key === 'animations' ? typeof value !== 'boolean'
      : !(choices[key as keyof typeof choices] as readonly unknown[]).includes(value))) {
      throw new Error('Unsupported appearance preference.');
    }
  }
  const raw = localStorage.getItem(getStorageKey(userId));
  const record = safeParseSettings(raw);
  if (raw !== null && !record) throw new Error('Saved settings could not be read. Nothing has been reset.');
  const previous = record?.appearance && typeof record.appearance === 'object' && !Array.isArray(record.appearance) ? record.appearance : {};
  // Preserve all other fields and the legacy notification-sync timestamp verbatim.
  localStorage.setItem(getStorageKey(userId), JSON.stringify({ ...record, _version: record?._version ?? 1, appearance: { ...previous, ...patch } }));
  window.dispatchEvent(new CustomEvent(APPEARANCE_CHANGED, { detail: { userId } }));
}
